import React, { useState } from 'react';
import _get from 'lodash/get';
import _partition from 'lodash/partition';
import _differenceBy from 'lodash/differenceBy';
import { useDispatch } from 'react-redux';
import { useData, useStatus } from 'resift';
import makeStyles from '@material-ui/core/styles/makeStyles';
import makePostFiltersGetUsers from 'fetches/makePostFiltersGetUsers';
import { Theme } from 'styles/ThemeTyping';
import * as Sift from '@sift/types';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Autocomplete from 'components/Autocomplete/PersonAutocomplete';
import EditMembersDropdown from 'pages/RolesAndPermissions/molecules/EditMembersDropdown';

interface Props {
  roleId: string;
  roleMembers: Array<any>;
  disabled: boolean;
  onUpdate: (actions: Array<any>) => void;
}

function ManuallyManagedMembers({ roleId, roleMembers, disabled, onUpdate }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeAutocomplete, setActiveAutocomplete] = useState<'include' | 'exclude' | null>(null);

  const getUsers = makePostFiltersGetUsers('editRoleMembers');
  const getUsersResponse = useData(getUsers) as Array<Sift.Person>;
  const getUsersStatus = useStatus(getUsers);
  const userSelectOptions = _get(getUsersResponse, ['results', 0, 'items'], []) as Array<
    Sift.Person
  >;

  const [includedMembersAnchorEl, setIncludedMembersAnchorEl] = useState<null | HTMLElement>(null);
  const [excludedMembersAnchorEl, setExcludedMembersAnchorEl] = useState<null | HTMLElement>(null);
  const [excludedUsers, includedUsers] = _partition(roleMembers, 'exclude');
  const [selectedUsers, setSelectedUsers] = useState<Array<Sift.Person>>([]);

  const handleViewIncludedMembers = (e: React.MouseEvent<HTMLElement>) => {
    setIncludedMembersAnchorEl(e.currentTarget);
    setExcludedMembersAnchorEl(null);
  };

  const handleViewExcludedMembers = (e: React.MouseEvent<HTMLElement>) => {
    setExcludedMembersAnchorEl(e.currentTarget);
    setIncludedMembersAnchorEl(null);
  };

  const handleSearch = (searchTerm: string) => {
    const exclude = activeAutocomplete === 'exclude';
    const existingIds = roleMembers.filter(rm => rm.exclude === exclude).map(rm => rm.personId);

    const filter = existingIds.length
      ? { field: 'id', comparison: 'neq', value: existingIds }
      : null;

    dispatch(getUsers(filter, searchTerm));
  };

  const addUser = (user: Sift.Person, exclude?: boolean) => {
    const newUser = {
      personId: user.id,
      roleId,
      exclude: exclude || false,
      _person: {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      },
    };

    const newUsers = !!roleMembers.find(u => u.personId === user.id)
      ? roleMembers
      : [...roleMembers, newUser];
    onUpdate(newUsers);

    setSelectedUsers(prevSelectedUsers => {
      return !!prevSelectedUsers.find(u => u.id === user.id)
        ? prevSelectedUsers
        : [...prevSelectedUsers, user];
    });
  };

  const removeUsers = (userIds: Array<string>) => {
    const newUsers = roleMembers.filter(user => !userIds.includes(user.personId));
    onUpdate(newUsers);

    setSelectedUsers(prevSelectedUsers =>
      prevSelectedUsers.filter(user => !userIds.includes(user.id)),
    );
    setIncludedMembersAnchorEl(null);
    setExcludedMembersAnchorEl(null);
  };

  return (
    <>
      <Box className={classes.addMemberBox}>
        <Typography className={classes.sectionTitle}>Explicitly Include People</Typography>
        <Typography gutterBottom className={classes.subtitle}>
          These people will always be included, regardless of whether they meet the dynamic filters.
        </Typography>
        <Autocomplete
          disabled={disabled}
          onFocus={() => setActiveAutocomplete('include')}
          dataSource={userSelectOptions.slice(0, 5).map(person => {
            return {
              object: person,
              selected: !!selectedUsers.find(user => user.id === person.id),
            };
          })}
          onChange={value => {
            handleSearch(value);
          }}
          onSelect={selectedItem => addUser(selectedItem)}
          placeholder="Search for a name"
          searchStatus={getUsersStatus}
          classes={{
            loaderRoot:
              activeAutocomplete === 'include'
                ? classes.loaderRoot
                : classes.blurredAutocompleteRoot,
          }}
        />

        <Typography variant="body2">
          Currently Included ({includedUsers.length} members):{' '}
          {includedUsers.length > 0 ? (
            <span onClick={handleViewIncludedMembers} className={classes.viewEditMembers}>
              View/Edit
            </span>
          ) : (
            <span>No members to view</span>
          )}
        </Typography>
      </Box>
      <Box>
        <Typography className={classes.sectionTitle}>Explicitly Exclude People</Typography>
        <Typography gutterBottom className={classes.subtitle}>
          These people will always be excluded, regardless of whether they meet the dynamic filters.
        </Typography>
        <Autocomplete
          disabled={disabled}
          onFocus={() => setActiveAutocomplete('exclude')}
          dataSource={userSelectOptions.slice(0, 5).map(person => {
            return {
              object: person,
              selected: !!selectedUsers.find(user => user.id === person.id),
            };
          })}
          onChange={value => {
            handleSearch(value);
          }}
          onSelect={selectedItem => addUser(selectedItem, true)}
          placeholder="Search for a name"
          searchStatus={getUsersStatus}
          classes={{
            loaderRoot:
              activeAutocomplete === 'exclude'
                ? classes.loaderRoot
                : classes.blurredAutocompleteRoot,
          }}
        />
        <Typography variant="body2">
          Currently Excluded ({excludedUsers.length} members):{' '}
          {excludedUsers.length > 0 ? (
            <span onClick={handleViewExcludedMembers} className={classes.viewEditMembers}>
              View/Edit
            </span>
          ) : (
            <span>No members to view</span>
          )}
        </Typography>
      </Box>
      <EditMembersDropdown
        anchorEl={includedMembersAnchorEl}
        menuItems={includedUsers.map(user => ({
          id: user.personId,
          displayName: `${_get(user, ['_person', 'firstName'], '')} ${_get(
            user,
            ['_person', 'lastName'],
            '',
          )}`,
        }))}
        onSubmit={removeUsers}
        onClose={() => setIncludedMembersAnchorEl(null)}
      />
      <EditMembersDropdown
        anchorEl={excludedMembersAnchorEl}
        menuItems={excludedUsers.map(user => ({
          id: user.personId,
          displayName: `${_get(user, ['_person', 'firstName'], '')} ${_get(
            user,
            ['_person', 'lastName'],
            '',
          )}`,
        }))}
        onSubmit={removeUsers}
        onClose={() => setExcludedMembersAnchorEl(null)}
      />
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  loaderRoot: {
    minHeight: 0,
  },
  blurredAutocompleteRoot: {
    opacity: 0,
    minHeight: 0,
  },
  subtitle: {
    ...theme.typography.body2,
    color: theme.colors.gray1,
  },
  sectionTitle: {
    fontWeight: theme.typography.fontWeight.semiBold,
  },
  addMemberBox: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  viewEditMembers: {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
  },
}));

export default ManuallyManagedMembers;
